import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Highlight, themes, Prism } from 'prism-react-renderer';
import { Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';

// Add bash/shell language support to Prism
// prism-react-renderer doesn't include bash by default
Prism.languages.bash = {
  comment: { pattern: /(^|[^"{\\$])#.*/, lookbehind: true },
  string: [
    { pattern: /\$'(?:[^'\\]|\\[\s\S])*'/, greedy: true },
    { pattern: /"(?:[^"\\$]|\\[\s\S]|\$(?!\()|`[^`]*`|\$\([^)]+\))*"/, greedy: true },
    { pattern: /'[^']*'/, greedy: true },
  ],
  variable: /\$(?:\w+|[!#?*@$]|\{[^}]+\})/,
  keyword: /\b(?:if|then|else|elif|fi|for|while|do|done|case|esac|function|in|select|until|export|readonly|local|unset|declare|set|echo|exit|return|source|exec|eval)\b/,
  builtin: /\b(?:cd|pwd|pushd|popd|dirs|let|read|printf|test|true|false|command|type|hash|bind|help|logout|times|umask|alias|unalias|bg|fg|jobs|kill|wait|disown|trap|ulimit|shopt|enable|complete|compgen|compopt|mapfile|readarray)\b/,
  boolean: /\b(?:true|false)\b/,
  operator: /&&|\|\||[<>]=?|[!=]=|[-+*/%]=?|[&|^~]/,
  punctuation: /[{}[\];(),.:]/,
  number: /\b\d+(?:\.\d+)?\b/,
  function: /\b\w+(?=\s*\(\))/,
};
Prism.languages.shell = Prism.languages.bash;

type SupportedLanguage = 'yaml' | 'json' | 'typescript' | 'python' | 'bash' | 'shell';

interface ConfigPreviewProps {
  code: string;
  language?: SupportedLanguage;
  title?: string;
  className?: string;
}

export function ConfigPreview({ code, language = 'yaml', title, className }: ConfigPreviewProps) {
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const languageLabel = language === 'typescript' ? 'TypeScript' : language.toUpperCase();

  return (
    <div className={cn('relative rounded-lg overflow-hidden', className)}>
      {title && (
        <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border/50">
          <span className="text-xs font-medium text-muted-foreground">{title}</span>
        </div>
      )}
      <div className="relative">
        <Highlight
          theme={themes.oneDark}
          code={code.trim()}
          language={language}
        >
          {({ style, tokens, getLineProps, getTokenProps }) => (
            <pre
              className="p-4 text-sm font-mono overflow-x-auto"
              style={{
                ...style,
                margin: 0,
                borderRadius: title ? '0 0 0.5rem 0.5rem' : '0.5rem',
              }}
            >
              {tokens.map((line, i) => (
                <div key={i} {...getLineProps({ line })}>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </div>
              ))}
            </pre>
          )}
        </Highlight>
        <div className="absolute top-2 right-2 flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground/70 uppercase tracking-wider bg-background/60 backdrop-blur-sm px-2 py-1 rounded">
            {languageLabel}
          </span>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={handleCopy}
            className="bg-background/80 backdrop-blur-sm hover:bg-muted/80"
            title="Copy to clipboard"
          >
            <AnimatePresence mode="wait" initial={false}>
              {copied ? (
                <motion.div
                  key="check"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Check className="h-3.5 w-3.5 text-emerald-500" />
                </motion.div>
              ) : (
                <motion.div
                  key="copy"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Copy className="h-3.5 w-3.5" />
                </motion.div>
              )}
            </AnimatePresence>
          </Button>
        </div>
      </div>
    </div>
  );
}
